import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, MapPin, Calendar, Tag, ArrowLeft, Target, Lightbulb, AlertCircle, CheckCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Get API URL - same logic as api.ts but without auth
const API_URL = (import.meta.env.VITE_API_URL || 'https://cityscrapeai-production.up.railway.app').replace(/\/+$/, '');

interface SharedPulse {
  id: number;
  title: string;
  source: string;
  category: string | null;
  date: string;
  what_changed: string;
  who_where_affects: string | null;
  impact_on_you: string | null;
  suggested_action: string | null;
  evidence_url: string | null;
  status: string | null;
  confidence: string | null;
  is_relevant: boolean | null;
}

export default function SharePulse() {
  // Get the share token from URL: /s/:code
  const { code } = useParams<{ code: string }>();

  const { data: pulse, isLoading, error } = useQuery({
    queryKey: ["shared-pulse", code],
    queryFn: async () => {
      if (!code) throw new Error('No share token provided');
      
      // Call the public endpoint with the token
      const url = `${API_URL}/api/pulses/s/${code}`;
      const response = await fetch(url);
      
      if (response.status === 410) {
        throw new Error('This share link has expired or been revoked');
      }
      if (!response.ok) {
        throw new Error('Pulse not found');
      }
      return response.json() as Promise<SharedPulse>;
    },
    enabled: !!code,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !pulse) {
    const errorMessage = error instanceof Error ? error.message : 'Pulse not found';
    const isExpired = errorMessage.includes('expired') || errorMessage.includes('revoked');
    
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md px-6">
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
            {isExpired ? 'Link Expired' : 'Pulse Not Found'}
          </h1>
          <p className="text-neutral-500">
            {isExpired 
              ? 'This share link has expired or been revoked by the owner.'
              : 'This pulse may have been removed or the link is invalid.'}
          </p>
          <Button variant="outline" onClick={() => window.location.href = "/"}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go to CityScrape
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
      {/* Header */}
      <header className="bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/cityscrape-logo.svg" alt="CityScrape" className="h-8 w-8" />
            <span className="font-semibold text-neutral-900 dark:text-white">CityScrape</span>
          </div>
          <Button variant="default" onClick={() => window.location.href = "/"}>
            Sign Up Free
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-lg overflow-hidden">
          {/* Pulse Header */}
          <div className="px-8 py-6 border-b border-neutral-200 dark:border-neutral-800">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                <MapPin className="h-3 w-3 mr-1" />
                {pulse.source}
              </Badge>
              {pulse.category && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                  <Tag className="h-3 w-3 mr-1" />
                  {pulse.category}
                </Badge>
              )}
              {pulse.status && (
                <Badge 
                  variant="outline" 
                  className={cn(
                    pulse.status === "pending" && "bg-yellow-50 text-yellow-700 border-yellow-200",
                    pulse.status === "approved" && "bg-green-50 text-green-700 border-green-200",
                    pulse.status === "Active" && "bg-blue-50 text-blue-700 border-blue-200"
                  )}
                >
                  {pulse.status}
                </Badge>
              )}
              {pulse.is_relevant === false && (
                <Badge variant="outline" className="bg-neutral-100 text-neutral-600 border-neutral-300">
                  Low Relevance
                </Badge>
              )}
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
              {pulse.title}
            </h1>
            <div className="flex items-center gap-2 text-sm text-neutral-500">
              <Calendar className="h-4 w-4" />
              {pulse.date}
            </div>
          </div>

          {/* Pulse Content */}
          <div className="px-8 py-6 space-y-6">
            {/* What Changed */}
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                What Changed
              </h2>
              <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed">
                {pulse.what_changed}
              </p>
            </div>

            {/* Who/Where It Affects */}
            {pulse.who_where_affects && (
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Who / Where It Affects
                </h2>
                <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed">
                  {pulse.who_where_affects}
                </p>
              </div>
            )}

            {/* Impact On You - Personalized Section */}
            {pulse.impact_on_you && (
              <div className={cn(
                "rounded-lg p-4 border",
                pulse.is_relevant === false 
                  ? "bg-neutral-50 dark:bg-neutral-800/50 border-neutral-200 dark:border-neutral-700"
                  : "bg-primary/5 border-primary/20"
              )}>
                <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3 flex items-center gap-2">
                  {pulse.is_relevant === false ? (
                    <CheckCircle className="h-4 w-4 text-neutral-500" />
                  ) : (
                    <Target className="h-4 w-4 text-primary" />
                  )}
                  Impact On You
                </h2>
                <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed">
                  {pulse.impact_on_you}
                </p>
              </div>
            )}

            {/* Suggested Action - Only shown if relevant */}
            {pulse.suggested_action && pulse.is_relevant !== false && (
              <div className="rounded-lg p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-green-700 dark:text-green-400 mb-3 flex items-center gap-2">
                  <Lightbulb className="h-4 w-4" />
                  Suggested Action
                </h2>
                <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed">
                  {pulse.suggested_action}
                </p>
              </div>
            )}

            {/* Confidence Level */}
            {pulse.confidence && (
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                  Confidence:
                </span>
                <Badge variant="outline" className={cn(
                  pulse.confidence === "High" && "bg-green-50 text-green-700 border-green-200",
                  pulse.confidence === "Medium" && "bg-yellow-50 text-yellow-700 border-yellow-200",
                  pulse.confidence === "Low" && "bg-red-50 text-red-700 border-red-200"
                )}>
                  {pulse.confidence}
                </Badge>
              </div>
            )}

            {/* Source Link - Prominent button */}
            {pulse.evidence_url && (
              <div className="p-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg border border-neutral-200 dark:border-neutral-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1">
                      View Source
                    </h3>
                    <p className="text-xs text-neutral-500">
                      See the original agenda item or document
                    </p>
                  </div>
                  <a
                    href={pulse.evidence_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open Source
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* CTA Footer */}
          <div className="px-8 py-6 bg-neutral-50 dark:bg-neutral-800/50 border-t border-neutral-200 dark:border-neutral-800">
            <div className="text-center space-y-4">
              <p className="text-neutral-600 dark:text-neutral-400">
                Get personalized insights for your development projects with CityScrape
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button variant="default" size="lg" onClick={() => window.location.href = "/"}>
                  Start Free Trial
                </Button>
                <Button variant="outline" size="lg" onClick={() => window.location.href = "https://www.cityscrape.ai"}>
                  Learn More
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-neutral-200 dark:border-neutral-800 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-neutral-500">
          <p>© {new Date().getFullYear()} CityScrape. Municipal intelligence, reimagined.</p>
        </div>
      </footer>
    </div>
  );
}
